import axios from "axios";
import { BASE_API_URL } from "../consts";

export interface MenuMealItem {
  _id?: string;
  name: string;
  description?: string | null;
  price: number;
  isAvailable: boolean;
}

export interface MenuMeal {
  _id: string;
  name: string;
  type: string;
  description?: string | null;
  currency: string;
  includedInPrice?: boolean;
  orderingRules?: string[];
  items: MenuMealItem[];
  extras?: MenuMealItem[];
  supplierNote?: string | null;
}

// Legacy fallback shape, only present when an apartment has no categories configured yet.
export interface MenuSupplier {
  _id: string;
  supplierId?: string;
  name: string;
  note?: string | null;
  meals: MenuMeal[];
}

export interface MenuCategoryItem {
  _id?: string;
  name: string;
  description?: string | null;
  price: number;
  currency: string;
  isAvailable: boolean;
}

export type MenuCategoryType = "contact" | "items" | "meals";

export interface MenuCategory {
  _id: string;
  name: string;
  emoji: string;
  description?: string | null;
  showPrices?: boolean;
  type: MenuCategoryType;
  // Exactly one of these is populated, matching `type`:
  contactPhone?: string;
  contactLabel?: string;
  items?: MenuCategoryItem[];
  currency?: string;
  meals?: MenuMeal[];
}

export interface MenuPageData {
  apartment: { _id: string; name: string; clientId: string };
  categories: MenuCategory[];
  suppliers: MenuSupplier[]; // legacy fallback if no categories configured
}

export interface OrderItem {
  mealId?: string;
  mealName?: string;
  itemName: string;
  price: number;
  quantity: number;
}

export interface SubmitOrderPayload {
  guestEmail: string;
  guestPhone: string;
  categoryId?: string;
  categoryName?: string;
  supplierId?: string;
  supplierName?: string;
  items: OrderItem[];
  total: number;
  currency?: string;
  guests?: number;
  days?: number;
  note?: string;
}

export type OrderStatus = "pending" | "confirmed" | "delivered" | "cancelled";

export interface MenuOrder {
  _id: string;
  clientId: string;
  apartmentId: string;
  apartmentName: string;
  categoryId?: string;
  categoryName?: string;
  supplierId?: string;
  supplierName?: string;
  guestEmail: string;
  guestPhone: string;
  items: OrderItem[];
  total: number;
  currency: string;
  note?: string;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MenuOrderFilters {
  apartmentId?: string;
  status?: OrderStatus;
  from?: string;
  to?: string;
}

export const getMenuPage = (aptId: string) =>
  axios.get<MenuPageData>(`${BASE_API_URL}/public/menu/${aptId}`);

export const submitMenuOrder = (aptId: string, data: SubmitOrderPayload) =>
  axios.post<{ success: boolean; orderId: string }>(`${BASE_API_URL}/public/menu/${aptId}/order`, data);

export const getMenuOrders = (clientId: string, filters?: MenuOrderFilters) =>
  axios.get<MenuOrder[]>(`${BASE_API_URL}/${clientId}/menu-orders`, { params: filters });

export const updateMenuOrderStatus = (orderId: string, status: OrderStatus) =>
  axios.put<MenuOrder>(`${BASE_API_URL}/menu-orders/${orderId}/status`, { status });
